/**
 * Event dispatcher — the single point where sim events leave the sim and fan
 * out to render-side juice systems.
 *
 * Sim events live in `world.state.events` (SimEvent[]). `main.ts` calls
 * `dispatcher.drain(sim.state.events)` right after `sim.step()` and then
 * CLEARS the array itself, so each event is delivered exactly once — even
 * across sim steps that return early without advancing (hitstop, paused).
 *
 * This dispatcher is ALSO the future audio attach point. Every juice system
 * registers handlers here; audio will register the same way.
 */

import type { SimEvent, VehicleKind } from "../sim/types";

/** A handler for one sim event type. */
export interface JuiceHandlers {
  onSpawn?: (vehicleId: number, kind: VehicleKind, role: "threat" | "ambient") => void;
  onDespawn?: (vehicleId: number) => void;
  /**
   * A kick contact. On a DOUBLE KICK (sim/kick.ts `pickKickTargets`) this fires
   * TWICE for the one sim event — once per vehicle, each with its own leg and
   * contact point — with `double === true` on both calls. Handlers that ignore
   * the 5th argument behave exactly as they did for a single kick.
   *
   * `aimX/aimZ` are the target vehicle's CENTRE OF MASS — where the leg
   * thrusts — and default to the contact point when the event omits them.
   * The contact point remains the burst/spark anchor.
   */
  onKick?: (
    vehicleId: number,
    leg: "left" | "right",
    contactX: number,
    contactZ: number,
    double?: boolean,
    aimX?: number,
    aimZ?: number,
  ) => void;
  onKickWhiff?: () => void;
  onNearMiss?: (vehicleId: number, margin: number) => void;
  onCrash?: (vehicleId: number) => void;
  onKickedVehicleDown?: (vehicleId: number, x: number, z: number) => void;
  /** First ground impact of a kicked vehicle — the dust-plume cue. */
  onKickedVehicleGroundHit?: (vehicleId: number, x: number, z: number) => void;
  onSpeedChange?: (forwardSpeed: number) => void;
  /**
   * A coin was collected. `x`/`z` are its world position at pickup — the juice
   * coordinator projects that to screen and launches the magnet sprite from it.
   */
  onCoin?: (coinId: number, x: number, z: number, value: number) => void;
}

/**
 * Event dispatcher. Register handlers via `add(handlers)`; drain the sim
 * event queue each tick via `drain(events)`.
 *
 * Zero allocation in steady state: the handlers array is fixed after setup;
 * drain iterates the sim's events array in place without copying.
 */
export class EventDispatcher {
  private readonly handlers: JuiceHandlers[] = [];

  add(handlers: JuiceHandlers): void {
    this.handlers.push(handlers);
  }

  /** Fan out all events in the queue to registered handlers. */
  drain(events: SimEvent[]): void {
    for (let i = 0; i < events.length; i++) {
      const e = events[i];
      for (let h = 0; h < this.handlers.length; h++) {
        const hd = this.handlers[h];
        switch (e.type) {
          case "spawn":
            hd.onSpawn?.(e.vehicleId, e.kind, e.role);
            break;
          case "despawn":
            hd.onDespawn?.(e.vehicleId);
            break;
          case "kick":
            hd.onKick?.(
              e.vehicleId,
              e.leg,
              e.contactX,
              e.contactZ,
              e.double === true,
              e.aimX ?? e.contactX,
              e.aimZ ?? e.contactZ,
            );
            if (
              e.double === true &&
              e.secondVehicleId !== undefined &&
              e.secondLeg !== undefined &&
              e.secondContactX !== undefined &&
              e.secondContactZ !== undefined
            ) {
              hd.onKick?.(
                e.secondVehicleId,
                e.secondLeg,
                e.secondContactX,
                e.secondContactZ,
                true,
                e.secondAimX ?? e.secondContactX,
                e.secondAimZ ?? e.secondContactZ,
              );
            }
            break;
          case "kickWhiff":
            hd.onKickWhiff?.();
            break;
          case "nearMiss":
            hd.onNearMiss?.(e.vehicleId, e.margin);
            break;
          case "crash":
            hd.onCrash?.(e.vehicleId);
            break;
          case "kickedVehicleDown":
            hd.onKickedVehicleDown?.(e.vehicleId, e.x, e.z);
            break;
          case "kickedVehicleGroundHit":
            hd.onKickedVehicleGroundHit?.(e.vehicleId, e.x, e.z);
            break;
          case "speedChange":
            hd.onSpeedChange?.(e.forwardSpeed);
            break;
          case "coin":
            hd.onCoin?.(e.coinId, e.x, e.z, e.value);
            break;
        }
      }
    }
  }
}
